import { Navigate } from "react-router-dom";
import PageTitle from "../page-header/PageHeader";
import { Card, Table, Tag, Statistic, Row, Col, Select, Input, Space, Progress, Alert } from "antd";
import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import {
	CheckCircleOutlined,
	WarningOutlined,
	ExclamationCircleOutlined,
} from "@ant-design/icons";

const { Option } = Select;

const ReviewCalibration = () => {
	const dispatch = useDispatch();
	const isLogged = Boolean(localStorage.getItem("isLogged"));
	const staffList = useSelector((state) => state.users?.list || []);
	const [selectedCycle, setSelectedCycle] = useState("All");
	const [selectedReviewer, setSelectedReviewer] = useState("All");

	const [calibrationData, setCalibrationData] = useState(() => {
		const storedReviews = localStorage.getItem("performanceReviews");
		if (storedReviews) {
			try {
				return JSON.parse(storedReviews);
			} catch (error) {
				console.error("Error parsing reviews:", error);
			}
		}
		return [];
	});

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	// Calculate calibration statistics
	const calibrationStats = useMemo(() => {
		const reviews = calibrationData.filter((review) => {
			const cycleMatch = selectedCycle === "All" || review.reviewCycle === selectedCycle;
			const reviewerMatch = selectedReviewer === "All" || review.reviewerId === parseInt(selectedReviewer);
			return cycleMatch && reviewerMatch && review.status === "Completed";
		});

		if (reviews.length === 0) return null;

		// Group by reviewer
		const byReviewer = {};
		reviews.forEach((review) => {
			const reviewerId = review.reviewerId;
			if (!byReviewer[reviewerId]) {
				byReviewer[reviewerId] = {
					reviewerId,
					reviewerName: review.reviewerName,
					reviews: [],
					totalReviews: 0,
					avgOverallRating: 0,
					avgJobKnowledge: 0,
					avgQualityOfWork: 0,
					avgProductivity: 0,
					avgCommunication: 0,
					avgTeamwork: 0,
					avgInitiative: 0,
					avgProblemSolving: 0,
					ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
				};
			}
			byReviewer[reviewerId].reviews.push(review);
			byReviewer[reviewerId].totalReviews++;
		});

		// Calculate averages and distributions
		Object.keys(byReviewer).forEach((reviewerId) => {
			const reviewer = byReviewer[reviewerId];
			const totals = reviewer.reviews.reduce(
				(acc, review) => ({
					overallRating: acc.overallRating + (review.overallRating || 0),
					jobKnowledge: acc.jobKnowledge + (review.jobKnowledge || 0),
					qualityOfWork: acc.qualityOfWork + (review.qualityOfWork || 0),
					productivity: acc.productivity + (review.productivity || 0),
					communication: acc.communication + (review.communication || 0),
					teamwork: acc.teamwork + (review.teamwork || 0),
					initiative: acc.initiative + (review.initiative || 0),
					problemSolving: acc.problemSolving + (review.problemSolving || 0),
				}),
				{
					overallRating: 0,
					jobKnowledge: 0,
					qualityOfWork: 0,
					productivity: 0,
					communication: 0,
					teamwork: 0,
					initiative: 0,
					problemSolving: 0,
				}
			);

			reviewer.avgOverallRating = (totals.overallRating / reviewer.totalReviews).toFixed(2);
			reviewer.avgJobKnowledge = (totals.jobKnowledge / reviewer.totalReviews).toFixed(2);
			reviewer.avgQualityOfWork = (totals.qualityOfWork / reviewer.totalReviews).toFixed(2);
			reviewer.avgProductivity = (totals.productivity / reviewer.totalReviews).toFixed(2);
			reviewer.avgCommunication = (totals.communication / reviewer.totalReviews).toFixed(2);
			reviewer.avgTeamwork = (totals.teamwork / reviewer.totalReviews).toFixed(2);
			reviewer.avgInitiative = (totals.initiative / reviewer.totalReviews).toFixed(2);
			reviewer.avgProblemSolving = (totals.problemSolving / reviewer.totalReviews).toFixed(2);

			// Rating distribution
			reviewer.reviews.forEach((review) => {
				const rating = Math.round(review.overallRating);
				if (rating >= 1 && rating <= 5) {
					reviewer.ratingDistribution[rating]++;
				}
			});
		});

		// Calculate overall averages
		const overallAvg = reviews.reduce(
			(acc, review) => acc + (review.overallRating || 0),
			0
		) / reviews.length;

		// Identify calibration issues
		const reviewers = Object.values(byReviewer);
		reviewers.forEach((reviewer) => {
			const diff = parseFloat(reviewer.avgOverallRating) - overallAvg;
			if (diff > 0.5) {
				reviewer.calibrationStatus = "Lenient";
				reviewer.calibrationColor = "orange";
			} else if (diff < -0.5) {
				reviewer.calibrationStatus = "Strict";
				reviewer.calibrationColor = "red";
			} else {
				reviewer.calibrationStatus = "Fair";
				reviewer.calibrationColor = "green";
			}
			reviewer.calibrationDifference = diff.toFixed(2);
		});

		return {
			totalReviews: reviews.length,
			overallAverage: overallAvg.toFixed(2),
			reviewers: reviewers,
		};
	}, [calibrationData, selectedCycle, selectedReviewer]);

	const columns = [
		{
			title: "Reviewer",
			dataIndex: "reviewerName",
			key: "reviewerName",
		},
		{
			title: "Total Reviews",
			dataIndex: "totalReviews",
			key: "totalReviews",
			align: "center",
		},
		{
			title: "Avg Overall Rating",
			dataIndex: "avgOverallRating",
			key: "avgOverallRating",
			align: "center",
			render: (rating) => (
				<Tag color={parseFloat(rating) >= 4 ? "green" : parseFloat(rating) >= 3 ? "blue" : "orange"}>
					{rating}/5
				</Tag>
			),
		},
		{
			title: "Calibration Status",
			dataIndex: "calibrationStatus",
			key: "calibrationStatus",
			align: "center",
			render: (status, record) => (
				<Tag color={record.calibrationColor} icon={status === "Fair" ? <CheckCircleOutlined /> : <WarningOutlined />}>
					{status}
				</Tag>
			),
		},
		{
			title: "Difference from Avg",
			dataIndex: "calibrationDifference",
			key: "calibrationDifference",
			align: "center",
			render: (diff) => {
				const value = parseFloat(diff);
				const color = value > 0 ? "green" : value < 0 ? "red" : "default";
				return (
					<Tag color={color}>
						{value > 0 ? "+" : ""}{diff}
					</Tag>
				);
			},
		},
		{
			title: "Rating Distribution",
			key: "distribution",
			render: (_, record) => (
				<div style={{ minWidth: 200 }}>
					{Object.keys(record.ratingDistribution).map((rating) => (
						<div key={rating} style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
							<span style={{ width: 20 }}>{rating}★:</span>
							<Progress
								percent={(record.ratingDistribution[rating] / record.totalReviews) * 100}
								showInfo={false}
								strokeColor={rating >= 4 ? "#52c41a" : rating >= 3 ? "#1890ff" : "#faad14"}
								size="small"
								style={{ flex: 1, marginLeft: 8 }}
							/>
							<span style={{ marginLeft: 8, fontSize: 12 }}>
								{record.ratingDistribution[rating]}
							</span>
						</div>
					))}
				</div>
			),
		},
	];

	const detailedColumns = [
		{
			title: "Reviewer",
			dataIndex: "reviewerName",
			key: "reviewerName",
			fixed: "left",
		},
		{
			title: "Reviews",
			dataIndex: "totalReviews",
			key: "totalReviews",
			align: "center",
		},
		{
			title: "Job Knowledge",
			dataIndex: "avgJobKnowledge",
			key: "avgJobKnowledge",
			align: "center",
		},
		{
			title: "Quality of Work",
			dataIndex: "avgQualityOfWork",
			key: "avgQualityOfWork",
			align: "center",
		},
		{
			title: "Productivity",
			dataIndex: "avgProductivity",
			key: "avgProductivity",
			align: "center",
		},
		{
			title: "Communication",
			dataIndex: "avgCommunication",
			key: "avgCommunication",
			align: "center",
		},
		{
			title: "Teamwork",
			dataIndex: "avgTeamwork",
			key: "avgTeamwork",
			align: "center",
		},
		{
			title: "Initiative",
			dataIndex: "avgInitiative",
			key: "avgInitiative",
			align: "center",
		},
		{
			title: "Problem Solving",
			dataIndex: "avgProblemSolving",
			key: "avgProblemSolving",
			align: "center",
		},
		{
			title: "Overall Avg",
			dataIndex: "avgOverallRating",
			key: "avgOverallRating",
			align: "center",
			render: (rating) => (
				<Tag color={parseFloat(rating) >= 4 ? "green" : parseFloat(rating) >= 3 ? "blue" : "orange"}>
					{rating}/5
				</Tag>
			),
		},
	];

	if (!isLogged) {
		return <Navigate to={"/admin/auth/login"} replace={true} />;
	}

	const uniqueCycles = ["All", ...new Set(calibrationData.map((r) => r.reviewCycle))];
	const uniqueReviewers = ["All", ...new Set(calibrationData.map((r) => r.reviewerId))];

	return (
		<div>
			<PageTitle title="Back" />
			<div style={{ padding: "20px" }}>
				<Card>
					<div className="text-center mb-4">
						<h2 className="text-2xl font-semibold text-gray-600">Review Calibration</h2>
						<p className="text-gray-500 mt-2">Ensure fair and consistent performance reviews across all reviewers</p>
					</div>

					{calibrationStats && (
						<>
							<Row gutter={16} className="mb-4">
								<Col span={6}>
									<Card>
										<Statistic
											title="Total Reviews"
											value={calibrationStats.totalReviews}
											prefix={<CheckCircleOutlined />}
										/>
									</Card>
								</Col>
								<Col span={6}>
									<Card>
										<Statistic
											title="Overall Average Rating"
											value={calibrationStats.overallAverage}
											suffix="/ 5"
											valueStyle={{ color: "#3f8600" }}
										/>
									</Card>
								</Col>
								<Col span={6}>
									<Card>
										<Statistic
											title="Reviewers Analyzed"
											value={calibrationStats.reviewers.length}
											prefix={<ExclamationCircleOutlined />}
										/>
									</Card>
								</Col>
								<Col span={6}>
									<Card>
										<Statistic
											title="Fair Calibrated"
											value={calibrationStats.reviewers.filter((r) => r.calibrationStatus === "Fair").length}
											suffix={`/ ${calibrationStats.reviewers.length}`}
											valueStyle={{ color: "#3f8600" }}
										/>
									</Card>
								</Col>
							</Row>

							<Space className="mb-4" style={{ width: "100%", justifyContent: "space-between" }}>
								<Space>
									<Select
										value={selectedCycle}
										onChange={setSelectedCycle}
										style={{ width: 200 }}
										placeholder="Filter by Review Cycle"
									>
										{uniqueCycles.map((cycle) => (
											<Option key={cycle} value={cycle}>
												{cycle}
											</Option>
										))}
									</Select>
									<Select
										value={selectedReviewer}
										onChange={setSelectedReviewer}
										style={{ width: 200 }}
										placeholder="Filter by Reviewer"
									>
										{uniqueReviewers.map((reviewerId) => {
											if (reviewerId === "All") {
												return <Option key="All" value="All">All Reviewers</Option>;
											}
											const reviewer = calibrationData.find((r) => r.reviewerId === reviewerId);
											return (
												<Option key={reviewerId} value={reviewerId.toString()}>
													{reviewer?.reviewerName || `Reviewer ${reviewerId}`}
												</Option>
											);
										})}
									</Select>
								</Space>
							</Space>

							{calibrationStats.reviewers.some((r) => r.calibrationStatus !== "Fair") && (
								<Alert
									message="Calibration Issues Detected"
									description="Some reviewers show significant deviation from the overall average. Review the calibration status below and consider calibration meetings."
									type="warning"
									showIcon
									className="mb-4"
								/>
							)}

							<Card title="Calibration Summary" className="mb-4">
								<Table
									columns={columns}
									dataSource={calibrationStats.reviewers}
									rowKey="reviewerId"
									pagination={false}
									scroll={{ x: true }}
								/>
							</Card>

							<Card title="Detailed Rating Breakdown">
								<Table
									columns={detailedColumns}
									dataSource={calibrationStats.reviewers}
									rowKey="reviewerId"
									pagination={false}
									scroll={{ x: true }}
								/>
							</Card>
						</>
					)}

					{!calibrationStats && (
						<Alert
							message="No Data Available"
							description="No completed reviews found for calibration analysis. Complete some performance reviews first."
							type="info"
							showIcon
						/>
					)}
				</Card>
			</div>
		</div>
	);
};

export default ReviewCalibration;

